import { useState, useEffect } from 'react'
import { X, Users, UserCheck, UserX, Search, Save, Loader2 } from 'lucide-react'
import api from '../services/api'

const CameraAssignmentModal = ({ isOpen, onClose, camera, onSuccess }) => {
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [users, setUsers] = useState([])
    const [roles, setRoles] = useState([])
    const [selectedUsers, setSelectedUsers] = useState([])
    const [selectedRoles, setSelectedRoles] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [activeTab, setActiveTab] = useState('users') // 'users' or 'roles'

    useEffect(() => {
        if (isOpen && camera) {
            fetchUsersAndRoles()
            // Load current assignments
            setSelectedUsers(camera.accessUsers?.map(user => user._id) || [])
            setSelectedRoles(camera.accessRoles?.map(role => role._id) || [])
        }
    }, [isOpen, camera])

    const fetchUsersAndRoles = async () => {
        try {
            setLoading(true)
            const [usersResponse, rolesResponse] = await Promise.all([
                api.get('/users?limit=1000'),
                api.get('/roles?limit=1000')
            ])
            
            setUsers(usersResponse.data.data.users.filter(user => user.isActive))
            setRoles(rolesResponse.data.data.roles)
        } catch (error) {
            console.error('Error fetching users and roles:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        try {
            setSaving(true)
            
            await api.put(`/cameras/${camera._id}`, {
                accessUsers: selectedUsers,
                accessRoles: selectedRoles
            })
            
            onSuccess()
            onClose()
        } catch (error) {
            console.error('Error updating camera assignments:', error)
            alert('Failed to update camera assignments. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    const toggleUser = (userId) => {
        setSelectedUsers(prev => 
            prev.includes(userId) 
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        )
    }

    const toggleRole = (roleId) => {
        setSelectedRoles(prev => 
            prev.includes(roleId) 
                ? prev.filter(id => id !== roleId)
                : [...prev, roleId]
        )
    }

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const filteredRoles = roles.filter(role =>
        role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (!isOpen || !camera) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] min-h-[600px] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Assign Camera</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            {camera.name} • {camera.location?.exactLocation || 'General Location'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-col flex-1 min-h-0">
                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('users')}
                            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'users'
                                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Users className="h-4 w-4 inline mr-2" />
                            Users ({selectedUsers.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('roles')}
                            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'roles'
                                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <UserCheck className="h-4 w-4 inline mr-2" />
                            Roles ({selectedRoles.length})
                        </button>
                    </div>

                    {/* Search */}
                    <div className="p-4 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder={`Search ${activeTab}...`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                    </div>

                    {/* List Content */}
                    <div className="flex-1 overflow-y-auto p-4 min-h-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                                <span className="ml-2 text-gray-600">Loading...</span>
                            </div>
                        ) : activeTab === 'users' ? (
                            <div className="space-y-2 max-h-full">
                                {filteredUsers.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                                        <p>No users found</p>
                                    </div>
                                ) : (
                                    filteredUsers.map(user => (
                                        <div
                                            key={user._id}
                                            onClick={() => toggleUser(user._id)}
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                                selectedUsers.includes(user._id)
                                                    ? 'bg-indigo-50 border-indigo-200'
                                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className={`w-3 h-3 rounded-full ${
                                                    selectedUsers.includes(user._id) ? 'bg-indigo-600' : 'bg-gray-300'
                                                }`}></div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{user.name || user.username}</p>
                                                    <p className="text-sm text-gray-500">{user.email}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {user.role && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        {user.role.name}
                                                    </span>
                                                )}
                                                {selectedUsers.includes(user._id) ? (
                                                    <UserCheck className="h-4 w-4 text-indigo-600" />
                                                ) : (
                                                    <UserX className="h-4 w-4 text-gray-400" />
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-full">
                                {filteredRoles.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <UserCheck className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                                        <p>No roles found</p>
                                    </div>
                                ) : (
                                    filteredRoles.map(role => (
                                        <div
                                            key={role._id}
                                            onClick={() => toggleRole(role._id)}
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                                selectedRoles.includes(role._id)
                                                    ? 'bg-indigo-50 border-indigo-200'
                                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className={`w-3 h-3 rounded-full ${
                                                    selectedRoles.includes(role._id) ? 'bg-indigo-600' : 'bg-gray-300'
                                                }`}></div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{role.name}</p>
                                                    <p className="text-sm text-gray-500">{role.description}</p>
                                                </div>
                                            </div>
                                            {selectedRoles.includes(role._id) ? (
                                                <UserCheck className="h-4 w-4 text-indigo-600" />
                                            ) : (
                                                <UserX className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                        <div className="text-sm text-gray-600">
                            {activeTab === 'users' ? (
                                <span>{selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected</span>
                            ) : (
                                <span>{selectedRoles.length} role{selectedRoles.length !== 1 ? 's' : ''} selected</span>
                            )}
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Assignments
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CameraAssignmentModal
